import {
  TuiDropdown,
  TuiFilterByInputPipe,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  type EffectCleanupRegisterFn,
  forwardRef,
  inject,
  input,
} from '@angular/core';
import {
  CdkFixedSizeVirtualScroll,
  CdkVirtualScrollViewport,
} from '@angular/cdk/scrolling';
import {
  TuiDataListWrapper,
  TuiStringifyContentPipe,
  TuiComboBox,
} from '@taiga-ui/kit';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Observable, of } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { stringifyFn } from '@core/helpers/stringifyFn';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'x-form-picker',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormPickerComponent),
      multi: true,
    },
  ],
  imports: [
    TuiTextfield,
    AsyncPipe,
    CdkFixedSizeVirtualScroll,
    CdkVirtualScrollViewport,
    TuiComboBox,
    TuiDataListWrapper,
    TuiFilterByInputPipe,
    TuiStringifyContentPipe,
    TuiDropdown,
    FormsModule,
  ],
  templateUrl: './form-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormPickerComponent implements ControlValueAccessor {
  private onTouched!: (value: any) => void;
  private onChange!: (value: any) => void;
  items$: Observable<any[]> = of([]);
  value: any;
  isDisabled = false;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  $stringifyFn = input<typeof stringifyFn>(stringifyFn, {
    alias: 'stringifyFn',
  });
  $withCleaner = input(false, {
    alias: 'withCleaner',
  });
  // true - только предлагаемые значения из выбора (items$)
  $strict = input(true, {
    alias: 'strict',
  });
  $readOnly = input(false, {
    alias: 'readOnly',
  });
  $initialItemId = input<number | null>(null, {
    alias: 'initialItemId',
  });

  constructor() {
    effect((onCleanup: EffectCleanupRegisterFn) => {
      const initialId = this.$initialItemId();

      if (initialId === null) {
        return;
      }

      const sub = this.items$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(items => {
          const selected = items.find(item => item.id === initialId);

          if (selected) {
            this.writeValue(selected);
            this.valueChanged(selected);
          }
        });

      onCleanup(() => sub.unsubscribe());
    });
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  writeValue(value: any) {
    this.value = value;
    // OnPush: без этого programmatic reset()/patchValue() не перерисовывает поле
    this.cdr.markForCheck();
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }

  valueChanged(value: any) {
    this.onChange?.(value);
    this.onTouched?.(value);
  }
}
